import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Select,
  Spinner,
  Switch,
  Tag,
  Text,
  Tooltip,
  useColorMode,
  useToast,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  AttachmentIcon,
  DeleteIcon,
  SmallCloseIcon,
} from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { RiSparkling2Fill, RiStickyNoteLine } from "react-icons/ri";
import extractUrls from "extract-urls";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";
import { renderMessageBody, renderAssistantBody } from "./supportBodySanitize";
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_MB,
  MAX_ATTACHMENT_BYTES,
  ALLOWED_ATTACHMENT_TYPES,
  ATTACHMENT_ACCEPT,
  BLOB_LOADING,
  OPAQUE_TYPE,
  safeContentType,
  isImageAttachment,
  attachmentPayload,
  formatBytes,
  newClientMessageId,
  uploadAttachment,
  attachmentBlobUrl,
} from "./supportAttachmentPolicy";

/**
 * Where an attachment is actually fetched from.
 *
 * NOT the URL stored on the message. That one points at the app's generic private-file
 * endpoint, which admits any member of the practice in any role — a pure patient of the
 * practice, holding the link, could read a file support's own rule says is staff-only.
 * The support route names the ticket, so the server can apply the rule that governs the
 * ticket, and it verifies the file is one that ticket actually carries.
 */
/**
 * How an attachment is cached and fetched: by MESSAGE as well as url.
 *
 * A url is not an identity. /upload honours a caller-chosen name for a private object, so
 * one thread can carry two messages naming the same object with different recorded
 * identities — and a cache keyed by url alone hands both of them whichever bytes were
 * fetched first, while the server, asked only for a url, answers with whichever record it
 * finds. Keyed by message, each one is fetched and judged on its own.
 */
/**
 * How an attachment is cached and fetched: by MESSAGE and POSITION.
 *
 * The server never sends the object's own url. That value is the storage key, and the
 * generic private-file endpoint admits any member of the practice in any role — so
 * anyone holding it reads a staff-only support file straight past the rule the support
 * endpoint exists to enforce. What arrives is where the attachment sits on its message,
 * which the server resolves against the thread.
 */
const attachmentKeyFor = (message, attachment) => `${message?._id || ""}|${attachment.ref}`;

const attachmentEndpoint = (ticketId, ref, messageId) =>
  `/support/inbox/${ticketId}/attachment?ref=${encodeURIComponent(ref)}`
  + (messageId ? `&message=${encodeURIComponent(messageId)}` : "");

import {
  heldSendsByScope,
  recordSend,
  dropSend,
  settleSend,
  offerableSend,
  hasOfferableSend,
  onHeldSendsChanged,
  retireStoredSends as retireHeldSends,
} from "./supportHeldSends";
import "./Support.css";

export { heldSendsByScope };

const SUPPORT_BADGE_TOPIC = "support_badge_update";
const INTERNAL_NOTE_WARNING = "Internal note — not visible to the customer";

// What an agent can MOVE a ticket to. `ai` is not on the list and must not be: the API
// refuses it, because escalation is one-way — a thread that reached a human does not
// quietly go back to the assistant, since the customer has been told somebody is looking
// at it. Offering it anyway just produced "Unknown status." Who answers next is the AI
// switch beside this, which is a different question and reversible.
const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "waiting_on_customer", label: "Waiting on customer" },
  { value: "closed", label: "Closed" },
];

// A ticket the assistant is still handling has a status that is not on that list. The
// select still has to show where the ticket IS, so it carries the current value as an
// unselectable entry rather than rendering blank or silently reading as "Open".
const STATUS_LABELS = {
  ai: "AI handled",
  open: "Open",
  waiting_on_customer: "Waiting on customer",
  closed: "Closed",
};

const STATUS_COLORS = {
  ai: "purple",
  open: "red",
  waiting_on_customer: "yellow",
  closed: "gray",
};


// Same shape as the inbox linkifier in Inbox/Messages.js: shelve existing markup
// so URLs inside attributes are never re-linkified, then restore it.


// A name (or any text) made safe to interpolate into innerHTML. Entities render as the
// characters they stand for, so the text still LOOKS identical — it is simply no longer
// PARSED as markup. Module-level because both the note renderer and the mention composer
// interpolate names, and they must escape the same way.
const escapeHtml = (text) => String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const sanitizedBody = (message) => {
  // Assistant prose is NOT linkified, and carries no anchors — the same rule the widget
  // applies to the same text. Only the `links` the server validated against the user's
  // navigation map are clickable, so a hallucinated or prompt-injected URL in the model's
  // words cannot become one. The console had been treating every role alike, which meant
  // a URL that stayed inert for the customer became clickable for the agent — offering a
  // privileged reader a destination nothing had checked, under the assistant's name.
  if (message.role === "assistant") return renderAssistantBody(message.body || "");

  // The note body is rendered exactly as any other message body — no inline
  // mention-bolding. Trying to bold each mentioned name by matching it in the free text
  // was a persistent source of defects: a case-insensitive substring `replace` bolded
  // the wrong word (`Ann` inside `Annabelle`), a name containing `$&`/`$1` was treated
  // as a replacement token, and escaping the name for HTML was then undone by
  // `hyperlinkUrls` decoding `&amp;`. It was also redundant — the "Notified:" line lists
  // the recipients authoritatively — and made the console render differently from the
  // customer widget, which the shared sanitizer exists to prevent. So it is gone: the
  // body is the body, on both surfaces, and who was notified is stated separately.
  return renderMessageBody(message.body || "");
};

// Every console write disables the page (composer, status, AI switch, Back) until it
// resolves. Without a deadline a hung request holds that lock forever — two minutes is
// past any plausible slow success, after which the failure path releases the page and
// holds the message for retry.
const WRITE_TIMEOUT_MS = 120000;

// Status and AI-pause changes are sent ONCE. They overwrite rather than append, and the
// transport retries a timed-out write: a slow-but-committed change, aborted at the
// deadline and sent again, re-applies an intent another console has already superseded
// — reopening what a colleague closed, re-pausing what they resumed. The server guards
// these writes against a customer message arriving underneath them, not against a stale
// duplicate of the agent's own request. Sends do not need this: their client id makes a
// second delivery a replay the server recognises.
const SEND_ONCE = { attempts: 1 };

const GENERIC_UPLOAD_ERROR = "That file could not be uploaded. Please try again.";



/** Empty when the file is fine, otherwise a sentence naming what is wrong with it. */
const attachmentRejection = (file, room) => {
  const name = file.name || "That file";
  if (room <= 0) return `You can attach up to ${MAX_ATTACHMENTS} files to a message.`;
  if (!ALLOWED_ATTACHMENT_TYPES.includes(String(file.type || "").toLowerCase())) {
    return `${name} is not a file type that can be attached.`;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) return `${name} is larger than ${MAX_ATTACHMENT_MB}MB.`;
  return "";
};

/** Only the four fields the API stores; the local bookkeeping never leaves here. */

// What the customer on this ticket calls the product; the server resolves it.
const brandLabels = (brand) => ({
  assistant: `${brand} assistant`,
  support: `${brand} support`,
  internal_note: "Internal note",
});

// The first-party name. Compared against, never rendered: it is how the console tells an
// agent that this customer sees something else.
const FIRST_PARTY_BRAND = "Viva";

// The brand this thread is answered under when the server could not name one. NEVER the
// first-party name: a practice whose brand did not resolve may well be white-labelled,
// which is the posture `brandForTicket` takes on the API side for the same reason.
const NEUTRAL_BRAND = "Support";

// The fallback for a role with no brand in it. `assistant` is deliberately absent: it is
// answered above, from the customer's brand, and an entry here would be a first-party
// name waiting for somebody to route through this branch.
const ROLE_LABELS = {
  support: "Support",
  internal_note: "Internal note",
};

/**
 * {role, author} — what the customer sees this message signed as, and the teammate
 * the audit trail says wrote it. For a human reply those are two different facts:
 * the reply reaches the customer under their own brand, which on a white-label
 * thread is not Viva, and an agent has to know which brand they are writing as
 * before they answer — while the console still has to say who sent it.
 */
const authorLabel = (message, ticket) => {
  // The assistant signs itself with the brand THIS customer knows, which for a
  // white-label practice is not Viva. The console shows the same name they see —
  // and where the server sent none, the neutral one, never ours.
  const labels = brandLabels(ticket?.customer_brand || NEUTRAL_BRAND);
  if (message.role === "assistant") return { role: labels.assistant };
  if (message.role === "user") {
    return { role: message.author_name || ticket?.user_name || "Customer" };
  }
  if (message.role === "support") {
    return { role: labels.support, author: message.author_name };
  }
  return { role: message.author_name || ROLE_LABELS[message.role] || "Support" };
};

/**
 * SS-4213. Everybody from the practice who is on this thread — every one of them sees it,
 * can answer it, and is told when we reply. The opener is marked. A superadmin adds the
 * practice's staff (picked from the same directory the outbound composer uses) and removes
 * people; the last participant cannot be removed. Rendered only when the API sends
 * `participants`, so an older API leaves the detail view exactly as it was.
 */
export const ParticipantsPanel = ({
  ticket, mutedColor, writing, saving, directory, directoryError, loadingDirectory, onLoadDirectory, onAdd, onRemove,
}) => {
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState("");
  const participants = ticket.participants || [];
  const onThread = new Set(participants.map((p) => String(p._id)));
  const candidates = ((directory && directory.staff) || []).filter((p) => !onThread.has(String(p._id)));
  const busy = writing || saving;
  return (
    <Box mt={2} textAlign="left" data-testid="support-participants">
      <Flex gap={1} wrap="wrap" align="center" maxH="72px" overflowY="auto">
        <Text fontSize="xs" color={mutedColor}>
          {participants.length === 1 ? "Participant:" : `Participants (${participants.length}):`}
        </Text>
        {participants.map((p) => (
          <Tag key={p._id} size="sm" variant="subtle" data-testid="support-participant">
            <Text as="span" fontSize="xs">
              {[p.name, p.email].filter(Boolean).join(" · ") || String(p._id)}
              {p.opener ? " (opened)" : ""}
            </Text>
            {participants.length > 1 && (
              <IconButton
                ml={1}
                size="xs"
                variant="ghost"
                minW="16px"
                h="16px"
                aria-label={`Remove ${p.name || p.email || "participant"}`}
                icon={<SmallCloseIcon />}
                isDisabled={busy}
                onClick={() => onRemove(p)}
              />
            )}
          </Tag>
        ))}
        {!adding && (
          <Button size="xs" variant="link" isDisabled={busy} onClick={() => { setAdding(true); onLoadDirectory(); }}>
            Add people
          </Button>
        )}
      </Flex>
      {adding && (
        <Flex gap={2} mt={1} align="center" wrap="wrap">
          {loadingDirectory && <Spinner size="xs" />}
          {!loadingDirectory && directoryError && (
            // A failed read is not "nobody": say so, and offer the read again.
            <Flex gap={2} align="center" data-testid="support-participant-directory-error">
              <Text fontSize="xs" color="red.500">{directoryError}</Text>
              <Button size="xs" variant="link" onClick={onLoadDirectory}>Try again</Button>
            </Flex>
          )}
          {!loadingDirectory && !directoryError && (
            <Select
              size="xs"
              width="260px"
              aria-label="Person to add to this thread"
              placeholder={candidates.length ? "Pick someone at this practice" : "Nobody else can be added"}
              value={picked}
              isDisabled={busy || !candidates.length}
              onChange={(e) => setPicked(e.target.value)}
              data-testid="support-participant-picker"
            >
              {candidates.map((p) => (
                <option key={p._id} value={p._id}>
                  {[p.name, p.email].filter(Boolean).join(" · ")}
                </option>
              ))}
            </Select>
          )}
          <Button
            size="xs"
            colorScheme="blue"
            isDisabled={!picked || busy}
            isLoading={saving}
            onClick={async () => {
              const ok = await onAdd(picked);
              if (ok) { setPicked(""); setAdding(false); }
            }}
          >
            Add
          </Button>
          <Button size="xs" variant="ghost" isDisabled={saving} onClick={() => { setPicked(""); setAdding(false); }}>
            Cancel
          </Button>
        </Flex>
      )}
    </Box>
  );
};

/**
 * SS-4211. A thread WE started: who sent it, the other threads of the same send, and —
 * when the practice accepted a change — what the change is and the button that says it
 * has been made. The note is staff-only; it never reaches the practice.
 */
export const OutboundSummary = ({ ticket, mutedColor, writing, savingAction, onOpen, onActionDone }) => {
  const outbound = ticket.outbound || {};
  const siblings = (outbound.group || []).filter((t) => String(t._id) !== String(ticket._id));
  const count = outbound.recipients || (outbound.group || []).length || 1;
  const sender = outbound.sent_by_name || (outbound.via === "tier2" ? "the assistant" : "support");
  return (
    <Box mt={2} textAlign="left" data-testid="support-outbound-summary">
      <Badge colorScheme="teal" textTransform="none" whiteSpace="normal">
        {`Outbound · sent by ${sender} · ${count} ${count === 1 ? "recipient" : "recipients"}`}
      </Badge>
      {siblings.length > 0 && (
        // Bounded like the note below: a send to many people must not push the thread out of view.
        <Flex gap={1} mt={1} wrap="wrap" align="center" maxH="64px" overflowY="auto"
          data-testid="support-outbound-siblings">
          <Text fontSize="xs" color={mutedColor}>Also sent to:</Text>
          {siblings.map((t) => (
            <Button key={t._id} size="xs" variant="link" onClick={() => onOpen(t._id)}>
              {t.user_name || t.user_email || String(t._id)}
            </Button>
          ))}
        </Flex>
      )}
      {!!outbound.action_note && (
        // Bounded: the note may run to 2,000 characters, and the header must never push the
        // thread and "Change applied" out of the ticket's fixed-height view.
        <Text fontSize="xs" color={mutedColor} mt={1} whiteSpace="pre-wrap" maxH="96px" overflowY="auto"
          data-testid="support-outbound-action-note">
          {`Change to make (staff only): ${outbound.action_note}`}
        </Text>
      )}
      {outbound.action_pending && (
        <Flex mt={2} gap={2} align="center" wrap="wrap">
          <Badge colorScheme="red" variant="solid">The practice accepted this change</Badge>
          <Button size="xs" colorScheme="green" isLoading={savingAction} isDisabled={writing} onClick={onActionDone}>
            Change applied
          </Button>
        </Flex>
      )}
    </Box>
  );
};

function SupportTicketThread({ ticketId }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { colorMode } = useColorMode();
  const { user, realtime, realtimeInitialized } = useAppContext();

  const [ticket, setTicket] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingAiPaused, setSavingAiPaused] = useState(false);
  // SS-4211: marking an accepted outbound change applied.
  const [savingAction, setSavingAction] = useState(false);
  // SS-4213: who is on the thread — the directory to add from, and a change in flight.
  const [savingParticipants, setSavingParticipants] = useState(false);
  const [participantDirectory, setParticipantDirectory] = useState(null);
  const [loadingParticipantDirectory, setLoadingParticipantDirectory] = useState(false);
  const [participantDirectoryError, setParticipantDirectoryError] = useState("");
  const [mode, setMode] = useState("reply");
  // The mode a switch is waiting on the agent to confirm; see switchMode. Null unless a
  // note-to-reply switch has something to disclose.
  const [pendingMode, setPendingMode] = useState(null);
  const cancelSwitchRef = useRef(null);
  const [mentioned, setMentioned] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  // Files picked for the reply or note being written: {_id, name, size,
  // content_type, uploading, url}. `url` arrives when the upload finishes.
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState("");
  // Private files cannot be loaded straight into an <img>: they need the bearer
  // token, so each one is fetched once and kept as an object URL.
  const [attachmentBlobs, setAttachmentBlobs] = useState({});

  const composerRef = useRef(null);
  // Live mirrors for the failure path. `holdOrRestore` runs long after the send began,
  // inside a closure whose `attachments`/`mentioned` are the PRE-send values — which the
  // send itself cleared — so reading the closure would call a genuinely empty composer
  // occupied and hold a message it could have restored. The mirrors are effect-updated,
  // which is current by the time any response lands.
  const attachmentsLiveRef = useRef([]);
  const mentionedLiveRef = useRef([]);
  const restoreRef = useRef(null);
  // A response can land after this page is gone. Nothing it does may touch composer
  // state — the registry is what carries the message from here on.
  const mountedRef = useRef(true);
  const maybeRestoreRef = useRef(null);
  // Defined below with the send machinery; `applySnapshot` sits above it.
  const retireStoredSendsRef = useRef(null);
  // The registry entry the composer is currently holding, if it came from the registry.
  // Sending supersedes it: either this IS that message going out again, or the agent
  // rewrote it — both make the old record obsolete at the same moment.
  const composerEntryRef = useRef(null);
  const timelineRef = useRef(null);
  const fetchRef = useRef(null);
  const fileInputRef = useRef(null);
  // Mirrors attachmentBlobs so the loader can tell what is already in flight
  // without re-running every time a blob lands.
  const attachmentBlobsRef = useRef({});
  // Raised by the teardown sweep below. A download still running when the thread
  // goes away — which a ticket change does, since the route remounts it — lands
  // after that sweep has already walked the map, so it has to revoke its own URL.
  const blobsDisposedRef = useRef(false);
  // Holds the send id of a failed attempt so a retry does not duplicate it.
  const retryRef = useRef(null);
  const pageRef = useRef(null);

  const isDark = colorMode === "dark";
  const pageBg = isDark ? "darkBackground" : "lightBackground";
  const panelBg = isDark ? "darkModalBackground" : "#FFFFFF";
  const borderClr = isDark ? "gray.600" : "gray.200";
  const mutedColor = isDark ? "gray.400" : "gray.600";
  const customerBg = isDark ? "#2C2F40" : "#E2F3F3";
  const supportBg = isDark ? "#2b4a63" : "#DCEBFA";
  const assistantBg = isDark ? "#2f3f36" : "#E4F3E7";
  const chipBg = isDark ? "#3A3E52" : "#F1F3F7";

  /**
   * Which snapshot of this thread is on screen.
   *
   * The badge topic this screen subscribes to is global — every support event anywhere
   * fires a re-fetch — so several requests are routinely in flight at once, and they do
   * not have to come back in the order they went out. Last-writer-wins means an older
   * one landing last, and the newest messages disappearing off the screen until the
   * next refresh brings them back.
   *
   * The visible case is worse than staleness: an agent presses Send, the reply lands and
   * renders, and a GET issued a moment BEFORE it answers afterwards with a thread that
   * has no such reply in it. Their own message vanishes. `client_message_id` means
   * retyping it will not duplicate it, but they have no way to know that.
   *
   * So each READ takes a number on the way out, and a response carrying an older one than
   * what is already rendered is dropped. The route remounts on a ticket change
   * (`key={ticketId}`), so this only ever orders one thread against itself.
   *
   * A WRITE is numbered differently — when its answer arrives, not when it was sent — and
   * the difference matters. Issue order is not commit order: a note POST sent first can
   * still be pre-commit when an unrelated global badge event issues a GET, and that GET
   * reads the thread without the note, returns first, and takes the higher number. The
   * note's own response is then discarded as stale even though it is the newer truth, the
   * composer has already been cleared, and an unmentioned note raises no badge event of
   * its own — so nothing ever repaints it. Numbering a write on the way back makes it
   * outrank every read that could not have seen it.
   */
  const issuedRef = useRef(0);
  const renderedRef = useRef(0);
  // The newest READ, specifically. "Newer than what is on screen" and "the newest one
  // asked for" differ exactly when the newest read FAILS: it paints nothing, so the
  // rendered mark does not move, and an older read — issued before the message that
  // caused the refresh — then passes and paints a thread without it. The badge topic
  // fires once per event, so nothing comes back for it. Writes are deliberately not
  // subject to this: `applyWrite` numbers an answer when it ARRIVES so it outranks every
  // read that could not have seen it, which is the opposite question.
  const newestReadRef = useRef(0);
  const issue = () => {
    const at = (issuedRef.current += 1);
    newestReadRef.current = at;
    return at;
  };
  const applySnapshot = useCallback((data, at, { fromRead = false } = {}) => {
    if (at < renderedRef.current) return;
    if (fromRead && at !== newestReadRef.current) return;
    renderedRef.current = at;
    retireStoredSendsRef.current?.(data);
    setTicket(data);
  }, []);
  /** A write's answer: newer than anything issued before it came back. */
  const applyWrite = useCallback((data) => applySnapshot(data, (issuedRef.current += 1)), [applySnapshot]);

  /**
   * One write at a time on this screen.
   *
   * `applyWrite` orders a write by when its ANSWER arrives, and that is only the newest
   * truth if no other write is also in flight. Two overlapping ones break it: the status
   * change commits and then waits on the notification fan-out, the AI-pause change
   * commits afterwards and returns first, and the status answer lands last carrying a
   * pre-pause snapshot — which then outranks even a GET that had already read the newer
   * state. The server is right and the console is wrong until some later event.
   *
   * Rather than trying to order writes against each other by guessing at commit times,
   * they are not allowed to overlap. Each control disables while ANY of them is running.
   * The cost is a moment where the header is inert during a reply; the alternative is a
   * screen that quietly disagrees with the database.
   */
  const writing = savingStatus || savingAiPaused || savingAction || savingParticipants || sending;

  const fetchTicket = useCallback(async () => {
    const at = issue();
    try {
      const response = await cqAPI.get(`/support/inbox/${ticketId}`);
      if (response?.type !== "ok" || !response.data) {
        toast({
          title: "Error",
          description: response?.msg || "Failed to load the ticket.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      applySnapshot(response.data, at, { fromRead: true });
    } catch (error) {
      console.error("Error loading support ticket:", error);
      toast({
        title: "Error",
        description: "Failed to load the ticket.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [ticketId, toast, applySnapshot]);

  useEffect(() => {
    fetchRef.current = fetchTicket;
  }, [fetchTicket]);

  useEffect(() => { attachmentsLiveRef.current = attachments; }, [attachments]);
  useEffect(() => { mentionedLiveRef.current = mentioned; }, [mentioned]);

  // A send that failed after this page unmounted — sidebar navigation, the browser's own
  // back button — is waiting in the module-level registry. Returning to the ticket
  // restores it into the fresh, empty composer, retry id and all. Through a ref, the way
  // fetchRef works here: the restorer is defined below with the send machinery, and the
  // effect wants "run once per ticket mount", not "re-run when the function identity
  // changes".
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // A send started by a page that is now gone settles somewhere else entirely. This is
  // how the message reaches the composer that happens to be open when that happens,
  // rather than waiting for the agent to navigate away and back.
  useEffect(() => onHeldSendsChanged(() => maybeRestoreRef.current?.()), []);

  useEffect(() => {
    // On the mount itself the composer is not rendered yet — the ticket has not loaded —
    // and the restorer declines when the composer is missing or occupied (keeping the
    // held record). The `!!ticket` dep refires this once the composer exists.
    maybeRestoreRef.current?.();
  }, [ticketId, !!ticket]);

  // A write in flight is the one moment leaving costs something that cannot be put back
  // client-side: the browser prompt is the only hold that covers tab close.
  useEffect(() => {
    if (!writing) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [writing]);

  useEffect(() => {
    setLoading(true);
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    cqAPI
      .get("/support/agents")
      .then((response) => {
        if (response?.type === "ok" && Array.isArray(response.data)) setAgents(response.data);
      })
      .catch((error) => console.error("Error loading support agents:", error));
  }, []);

  useEffect(() => {
    const client = realtime.current;
    if (!client) return undefined;
    const handler = () => fetchRef.current && fetchRef.current();
    client.subscribeToTopic(SUPPORT_BADGE_TOPIC, null, handler);
    return () => client.unsubscribeFromTopic(SUPPORT_BADGE_TOPIC, null, handler);
  }, [realtimeInitialized, realtime]);

  useEffect(() => {
    if (!timelineRef.current) return;
    timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [ticket]);

  // Attached images are private files behind the authenticated /file endpoint, so
  // each one is fetched once and kept as an object URL for its thumbnail. Only
  // images are pre-loaded; anything else is fetched when it is actually opened.
  useEffect(() => {
    // url -> the type its blob is built with, so the same file queued twice is
    // only fetched once.
    const pending = new Map();
    (ticket?.messages || [])
      .flatMap((message) => (message.attachments || []).map(a => ({ a, message })))
      .filter(({ a }) => a && a.ref != null && isImageAttachment(a))
      .forEach(({ a, message }) => {
        const key = attachmentKeyFor(message, a);
        if (attachmentBlobsRef.current[key] || pending.has(key)) return;
        pending.set(key, { ref: a.ref, type: safeContentType(a), messageId: message?._id });
      });

    pending.forEach(({ ref, type, messageId }, key) => {
      attachmentBlobsRef.current[key] = BLOB_LOADING;
      attachmentBlobUrl(attachmentEndpoint(ticketId, ref, messageId), type)
        .then((blobUrl) => {
          if (!blobUrl) {
            delete attachmentBlobsRef.current[key];
            return;
          }
          if (blobsDisposedRef.current) {
            window.URL.revokeObjectURL(blobUrl);
            return;
          }
          attachmentBlobsRef.current[key] = blobUrl;
          setAttachmentBlobs((current) => ({ ...current, [key]: blobUrl }));
        })
        .catch((error) => {
          delete attachmentBlobsRef.current[key];
          console.error("Error loading a support attachment:", error);
        });
    });
  }, [ticket]);

  // Object URLs live until the tab is closed otherwise.
  useEffect(() => {
    blobsDisposedRef.current = false;
    const blobs = attachmentBlobsRef.current;
    return () => {
      blobsDisposedRef.current = true;
      Object.values(blobs).forEach((url) => {
        if (url && url !== BLOB_LOADING) window.URL.revokeObjectURL(url);
      });
    };
  }, []);

  const adjustComposerHeight = () => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const setCursorAfterHtmlInsert = (contentEle) => {
    const range = document.createRange();
    const selection = window.getSelection();
    range.setStart(contentEle, contentEle.childNodes.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const insertAgentNameAtCursor = (member) => {
    setMentioned([...mentioned, member]);
    const currentValue = composerRef.current.innerHTML;
    const lastAtPos = currentValue.lastIndexOf("@");
    const beforeAt = currentValue.substring(0, lastAtPos);
    composerRef.current.innerHTML = `${beforeAt}<b>${escapeHtml(member.name)}</b>&nbsp;`;
    setCursorAfterHtmlInsert(composerRef.current);
    setSuggestions([]);
  };

  const deleteMention = (member) => {
    // Remove ONE bold node whose decoded text is this name — matched by textContent, not
    // by string-searching innerHTML, which the browser re-serializes (`&#39;` back to a
    // literal apostrophe, so `O'Neil` never matched). Exactly one, because two distinct
    // superadmins can share a display name: removing every matching `<b>` would erase
    // the other person's chip from the note while React state still lists them, so the
    // note would name one fewer person than it actually notifies.
    const el = composerRef.current;
    if (el) {
      const match = [...el.querySelectorAll("b")].find((node) => node.textContent === member.name);
      if (match) match.remove();
    }
    setCursorAfterHtmlInsert(el);
    // Drop this member by id, once. `mentioned` is deduplicated by id, so filtering by id
    // removes exactly the one clicked even when a namesake remains.
    setMentioned(mentioned.filter((m) => m._id !== member._id));
  };

  const checkMention = () => {
    if (mode !== "note") {
      if (suggestions.length) setSuggestions([]);
      return;
    }
    const currentValue = composerRef.current.innerText;
    const lastAtPos = currentValue.lastIndexOf("@");

    if (lastAtPos !== -1 && /@[a-zA-Z ]{1,14}$/.test(currentValue)) {
      const searchQuery = currentValue.substring(lastAtPos + 1).toLowerCase();
      const matching = agents
        .filter((a) =>
          (a.name || "")
            .toLowerCase()
            .split(" ")
            .some((part) => part.startsWith(searchQuery))
        )
        .filter((a) => !mentioned.find((m) => m._id === a._id))
        .slice(0, 3);
      setSuggestions((prev) =>
        prev.length === matching.length && prev.every((p, i) => p._id === matching[i]._id)
          ? prev
          : matching
      );
    } else {
      setSuggestions([]);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastingText = event.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    const textNode = document.createTextNode(pastingText);
    range.deleteContents();
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleBlur = () => {
    if (composerRef.current?.innerText) return;
    setPlaceholderVisible(true);
  };

  const applyMode = (nextMode) => {
    setMode(nextMode);
    setSuggestions([]);
    if (nextMode === "reply" && mentioned.length) setMentioned([]);
  };

  /**
   * Moving a draft between the two audiences, which is not a display preference.
   *
   * The composer is one box serving two destinations, and its contents survive the
   * switch — an agent halfway through a note who realises it should be a reply keeps
   * what they wrote, which is the point. But in the note-to-reply direction that same
   * convenience is a disclosure: staff-only words, and any file staged with them, become
   * a customer-visible message and an email, on one click that says only "Reply". The
   * panel changing from orange to blue is the whole warning, and it arrives after the
   * fact.
   *
   * So the consequence is named before it happens, and only when there is something to
   * disclose — an empty composer switches freely, which is the ordinary case. Nothing is
   * thrown away either way: Cancel leaves the note exactly as it was.
   *
   * The reverse direction is deliberately unguarded. Turning a reply into a note withholds
   * a message rather than exposing one, and it announces itself: the panel turns orange,
   * the stored note renders in its own colour under an "Internal note" label, and the
   * ticket does not move to `waiting_on_customer`. The agent can see it and send again.
   */
  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    const el = composerRef.current;
    const carries = !!(el && (el.innerText || "").trim())
      || attachmentsLiveRef.current.length > 0;
    if (nextMode === "reply" && mode === "note" && carries) {
      setPendingMode(nextMode);
      return;
    }
    applyMode(nextMode);
  };

  const confirmSwitchMode = () => {
    const next = pendingMode;
    setPendingMode(null);
    if (next) applyMode(next);
  };

  // A write that overwrites is sent once, so a failure is genuinely ambiguous: the
  // change may have committed and lost its answer on the way back. Reading the ticket
  // again is the only way to know, and `applySnapshot` orders it against everything
  // else in flight.
  const resyncAfterFailedWrite = () => {
    if (mountedRef.current) fetchRef.current?.();
  };

  const handleStatusChange = async (nextStatus) => {
    setSavingStatus(true);
    try {
      // The customer's last message as this screen has it. The server refuses the
      // change if one has arrived since — closing a ticket on top of a reply the agent
      // never saw buries it, because a closed ticket is out of the default inbox.
      const response = await cqAPI.put(
        `/support/inbox/${ticketId}/status`,
        {
          status: nextStatus,
          lastCustomerMessageAt: ticket?.last_customer_message_at ?? null,
          // ...and which message that was. Two customer messages a millisecond apart
          // carry the same timestamp, so the time alone cannot tell the server whether
          // this screen has seen the newer one.
          lastCustomerMessageId: ticket?.last_customer_message_id ?? null,
        },
        WRITE_TIMEOUT_MS,
        SEND_ONCE
      );

      if (response?.type === "ok" && response.data) {
        applyWrite(response.data);
      } else {
        // A refusal can carry the ticket as it now stands; take it, so the agent is
        // looking at the message that caused the refusal rather than at a stale view.
        if (response?.data) applyWrite(response.data);
        // A refusal that carries no ticket is a transport failure, and this write is
        // sent once: it may have committed and lost its answer. Read the ticket back so
        // the screen shows what the server actually holds rather than the value the
        // agent tried to set.
        else resyncAfterFailedWrite();
        toast({
          title: "Error",
          description: response?.msg || "Failed to update the status.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating support ticket status:", error);
      resyncAfterFailedWrite();
      toast({
        title: "Error",
        description: "Failed to update the status.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      if (mountedRef.current) setSavingStatus(false);
    }
  };

  // The switch renders off the stored ticket and nothing else, so a request that
  // fails leaves it where it was rather than showing a pause the server never took.
  const handleAiPausedChange = async (paused) => {
    setSavingAiPaused(true);
    try {
      const response = await cqAPI.put(`/support/inbox/${ticketId}/ai`, { paused }, WRITE_TIMEOUT_MS, SEND_ONCE);
      if (response?.type === "ok" && response.data) {
        applyWrite(response.data);
      } else {
        // Sent once, so a failure leaves the true value unknown — the switch must show
        // the server's answer, not the one the agent asked for.
        resyncAfterFailedWrite();
        toast({
          title: "Error",
          description: response?.msg || "Failed to change who answers this thread.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating support ticket AI pause:", error);
      resyncAfterFailedWrite();
      toast({
        title: "Error",
        description: "Failed to change who answers this thread.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      if (mountedRef.current) setSavingAiPaused(false);
    }
  };

  // SS-4213. The practice's staff who could be put on this thread — the same directory the
  // outbound composer reads, loaded when somebody asks to add people.
  const loadParticipantDirectory = async () => {
    if (!ticket?.bid || participantDirectory || loadingParticipantDirectory) return;
    setLoadingParticipantDirectory(true);
    setParticipantDirectoryError("");
    try {
      const response = await cqAPI.get(`/support/inbox/recipients?bid=${encodeURIComponent(ticket.bid)}`);
      if (!mountedRef.current) return;
      if (response?.type === "ok") setParticipantDirectory(response.data || {});
      else setParticipantDirectoryError(response?.msg || "Could not load the practice's staff.");
    } catch (error) {
      console.error("Error loading the practice's staff:", error);
      if (mountedRef.current) setParticipantDirectoryError("Could not load the practice's staff.");
    } finally {
      if (mountedRef.current) setLoadingParticipantDirectory(false);
    }
  };

  // Sent once and read back on failure, like every other write that changes the ticket.
  const changeParticipants = async (request, failure) => {
    setSavingParticipants(true);
    try {
      const response = await request();
      if (response?.type === "ok" && response.data) {
        applyWrite(response.data);
        return true;
      }
      resyncAfterFailedWrite();
      toast({ title: "Error", description: response?.msg || failure, status: "error", duration: 4000, isClosable: true });
      return false;
    } catch (error) {
      console.error("Error changing support ticket participants:", error);
      resyncAfterFailedWrite();
      toast({ title: "Error", description: failure, status: "error", duration: 4000, isClosable: true });
      return false;
    } finally {
      if (mountedRef.current) setSavingParticipants(false);
    }
  };
  const handleAddParticipant = (uid) => changeParticipants(
    () => cqAPI.post(`/support/inbox/${ticketId}/participants`, { uids: [uid] }, WRITE_TIMEOUT_MS, SEND_ONCE),
    "Could not add them to this thread.",
  );
  const handleRemoveParticipant = (participant) => changeParticipants(
    () => cqAPI.del(`/support/inbox/${ticketId}/participants/${participant._id}`, {}, WRITE_TIMEOUT_MS, SEND_ONCE),
    "Could not remove them from this thread.",
  );

  // SS-4211. The practice accepted a change on a thread we started, and somebody has now
  // made it. The only thing that clears the pending change: the server tells the practice
  // and closes the ticket. Sent once, like the status change, and read back on failure.
  const handleActionDone = async () => {
    setSavingAction(true);
    try {
      // The customer's last message as this screen has it, exactly as a status change
      // sends it: this closes the ticket, so the server refuses it on top of a reply the
      // agent has not seen.
      const response = await cqAPI.put(
        `/support/inbox/${ticketId}/action`,
        {
          done: true,
          lastCustomerMessageAt: ticket?.last_customer_message_at ?? null,
          lastCustomerMessageId: ticket?.last_customer_message_id ?? null,
        },
        WRITE_TIMEOUT_MS,
        SEND_ONCE
      );
      if (response?.type === "ok" && response.data) {
        applyWrite(response.data);
        toast({ title: "Change applied — the practice has been told.", status: "success", duration: 3000, isClosable: true });
      } else {
        if (response?.data) applyWrite(response.data);
        else resyncAfterFailedWrite();
        toast({
          title: "Error",
          description: response?.msg || "Failed to mark the change applied.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error marking an outbound change applied:", error);
      resyncAfterFailedWrite();
      toast({ title: "Error", description: "Failed to mark the change applied.", status: "error", duration: 3000, isClosable: true });
    } finally {
      if (mountedRef.current) setSavingAction(false);
    }
  };

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return undefined;
    // The thread is a fixed-height flex column with a pinned composer, so its
    // height has to be the viewport MINUS wherever the app shell places it.
    // Assuming it starts at y=0 pushed the composer below the fold on mobile.
    const applyOffset = () => {
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top));
      el.style.setProperty("--support-ticket-top", `${top}px`);
    };
    applyOffset();
    window.addEventListener("resize", applyOffset);
    return () => window.removeEventListener("resize", applyOffset);
    // Keyed on `ticket`: the container does not exist during the initial load,
    // so a mount-only effect would measure nothing.
  }, [ticket]);

  const removeAttachment = (id) => {
    setAttachments((current) => current.filter((a) => a._id !== id));
    setAttachError("");
  };

  // Files are uploaded as they are picked, so the send itself only carries the
  // URLs the endpoint gave back. A file the server would refuse is turned away
  // here, and the ones that are fine still go up.
  const attachFiles = async (files) => {
    const picked = Array.from(files || []);
    if (!picked.length) return;

    let room = MAX_ATTACHMENTS - attachments.length;
    let rejection = "";
    const queued = [];

    picked.forEach((file) => {
      const problem = attachmentRejection(file, room);
      if (problem) {
        rejection = rejection || problem;
        return;
      }
      room -= 1;
      queued.push({
        file,
        entry: {
          _id: newClientMessageId(),
          name: file.name || "Attachment",
          size: file.size || 0,
          content_type: String(file.type || "").toLowerCase(),
          uploading: true,
        },
      });
    });

    setAttachError(rejection);
    if (!queued.length) return;
    setAttachments((current) => [...current, ...queued.map((q) => q.entry)]);

    for (const { file, entry } of queued) {
      try {
        const response = await uploadAttachment(file, ticket?.bid);
        if (response?.type !== "ok" || !response.url) {
          setAttachments((current) => current.filter((a) => a._id !== entry._id));
          setAttachError(response?.msg || GENERIC_UPLOAD_ERROR);
          continue;
        }
        setAttachments((current) =>
          current.map((a) => (a._id === entry._id ? { ...a, uploading: false, url: response.url } : a))
        );
      } catch (error) {
        console.error("Error uploading a support attachment:", error);
        setAttachments((current) => current.filter((a) => a._id !== entry._id));
        setAttachError(GENERIC_UPLOAD_ERROR);
      }
    }
  };

  // A private file cannot be linked to directly — it needs the bearer token — so
  // it is fetched and handed to the browser. An image is already in the object-URL
  // map from rendering its thumbnail, and opening that costs nothing; anything
  // else is downloaded, because a tab opened after the fetch would be swallowed
  // by the popup blocker.
  const openAttachment = async (attachment, message) => {
    // `ref` can legitimately be 0 — most messages carry one attachment — so this is a
    // presence test, never a truthiness one.
    if (!attachment || attachment.ref == null) return;
    const type = safeContentType(attachment);
    const cached = attachmentBlobsRef.current[attachmentKeyFor(message, attachment)];
    // A tab is only ever opened on a file whose recorded type we trust.
    if (type && cached && cached !== BLOB_LOADING) {
      window.open(cached, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const blobUrl = await attachmentBlobUrl(attachmentEndpoint(ticketId, attachment.ref, message?._id), type || OPAQUE_TYPE);
      if (!blobUrl) {
        toast({
          title: "Error",
          description: "That file could not be opened.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", attachment.name || "attachment");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error opening a support attachment:", error);
      toast({
        title: "Error",
        description: "That file could not be opened.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Put one captured send back in front of the agent: mode, text, files, mentions, and
  // the retry identity that makes re-sending it safe.
  const applyHeldSend = (held) => {
    if (!composerRef.current) return false;
    setMode(held.mode);
    composerRef.current.innerText = held.message;
    adjustComposerHeight();
    setPlaceholderVisible(!held.message);
    setAttachments(held.attachments);
    setMentioned(held.mentions);
    attachmentsLiveRef.current = held.attachments;
    mentionedLiveRef.current = held.mentions;
    retryRef.current = {
      scope: held.scope,
      body: held.message,
      attachmentKey: held.attachmentKey,
      mentionKey: held.mentionKey,
      clientMessageId: held.clientMessageId,
    };
    return true;
  };

  // Which queue a send belongs to. The signed-in user is part of the key because the
  // registry is module state and outlives a session: one agent signing out and another
  // signing in on the same tab must not find the first one's unsent words — private
  // notes, a customer reply, and a retry token that would file them under the new
  // agent's name — waiting in their composer.
  const sendScope = (forMode) => `${user?.id || "anon"}:${ticketId}:${forMode}`;

  // Take the OLDEST offerable held send for this scope into the composer. A missing
  // composer leaves it recorded for the next chance.
  const restoreHeldSend = (scope) => {
    const entry = offerableSend(scope);
    if (!entry) return;
    if (!applyHeldSend(entry)) return;
    // Kept on the books. A message sitting in a composer is one navigation away from
    // being lost — and an ADOPTED one may still have a live request behind it that will
    // never confirm — so the record stands until the server takes it or the agent sends
    // something in its place.
    composerEntryRef.current = entry;
  };
  restoreRef.current = restoreHeldSend;

  /**
   * Retire every token this thread proves the server already has.
   *
   * A token is spent once its message is stored, but the console could only learn that
   * from the send's own answer — and the sends that most need retiring are the ones
   * whose answer never came. The message sits in the thread while this page still
   * believes it never sent: it keeps offering it back, and an agent who types those same
   * words again spends the same token on a message the server refuses as a duplicate,
   * so their second note is swallowed without a word.
   */
  const retireStoredSends = (data) => {
    const stored = new Set(
      (data?.messages || []).map(m => m.client_message_id).filter(Boolean)
    );
    if (!stored.size) return;

    if (retryRef.current && stored.has(retryRef.current.clientMessageId)) retryRef.current = null;
    if (composerEntryRef.current && stored.has(composerEntryRef.current.clientMessageId)) {
      // The WORDS as well as the token. Retiring the identity on its own left the message
      // in the composer with nothing left to recognise it by: the agent reads a reply they
      // were told had failed, presses Send, and — the retry record having just been
      // cleared — it goes out under a FRESH id as a second copy of something already on the
      // thread. For a reply that means a second email to the customer; for a note, a second
      // mention alert to a colleague.
      //
      // Only while it is still exactly what was handed back. An edit makes it the agent's
      // next message, and theirs to send.
      const entry = composerEntryRef.current;
      const el = composerRef.current;
      const unedited = !!el
        && el.innerText === entry.message
        && attachmentsLiveRef.current.map(a => a.url).join("|") === entry.attachmentKey
        && mentionedLiveRef.current.map(m => String(m._id)).sort().join("|") === entry.mentionKey;
      if (unedited) {
        el.innerText = "";
        adjustComposerHeight();
        setAttachments([]);
        setMentioned([]);
        setSuggestions([]);
        setPlaceholderVisible(true);
        attachmentsLiveRef.current = [];
        mentionedLiveRef.current = [];
        toast({
          title: "Already sent",
          description: "That message did go through after all — it's on the thread above.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      }
      composerEntryRef.current = null;
    }
    // Through the registry, which announces the change — so a send queued BEHIND the one
    // that just left is offered back rather than stranded in module memory, invisible,
    // and lost on the next reload. This page already listens for that announcement.
    retireHeldSends(stored);
  };
  retireStoredSendsRef.current = retireStoredSends;

  /**
   * Restore a held send for this ticket, if the composer is free to receive it.
   *
   * Called at the moments the composer naturally empties: the ticket (re)mounting, and a
   * draft sending successfully. NOT on manual clearing — an agent who selects-all-deletes
   * to write something new does not want the held message forced back under their cursor.
   */
  const maybeRestoreHeld = () => {
    const el = composerRef.current;
    const free = !!el && !(el.innerText || "").trim() && !attachmentsLiveRef.current.length && !mentionedLiveRef.current.length;
    if (!free) return;
    for (const heldMode of ["note", "reply"]) {
      if (hasOfferableSend(sendScope(heldMode))) {
        restoreHeldSend(sendScope(heldMode));
        return;
      }
    }
  };
  maybeRestoreRef.current = maybeRestoreHeld;

  /**
   * Put a failed send back in front of the agent — whole, or not yet.
   *
   * Restored WHOLE (text, files, mentions, mode, retry id) when the composer is free;
   * held when it is not, because merging a failed note into a half-written reply mixes
   * two messages. A held send is restored by the mount effect when the agent returns to
   * the ticket with an empty composer. The retry id rides along so re-sending a message
   * that actually committed (response lost) is recognised by the server as a duplicate.
   */
  const holdOrRestore = (entry, why) => {
    // The page that sent it may be gone. The entry stays where it is, now offerable,
    // and whichever page is on screen is told to look.
    settleSend(entry, { orphaned: !mountedRef.current });
    if (!mountedRef.current) return;
    const el = composerRef.current;
    const composerFree = !!el && !(el.innerText || "").trim() && !attachmentsLiveRef.current.length && !mentionedLiveRef.current.length;
    // Restored directly, never through the queue: with an older message already queued
    // under this scope, the queue's front is that one — but the agent just watched THIS
    // send fail, and the toast promises THIS message back.
    if (composerFree && applyHeldSend(entry)) {
      composerEntryRef.current = entry;
      toast({
        title: "Error",
        description: `${why} Your message is back in the composer.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      // It stays queued — and the retry identity goes with it. What is in the composer
      // now is a DIFFERENT message the agent typed meanwhile; leaving the failed send's
      // identity live would let an identical-looking draft inherit its
      // `clientMessageId`, and the server, seeing the id again, would store the first
      // and silently accept the second as a replay of it.
      retryRef.current = null;
      toast({
        title: "Error",
        description: `${why} Your message is held — it returns to the composer once your current draft is sent, or when you come back to this ticket.`,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    }
  };

  const handleSend = async () => {
    const message = composerRef.current?.innerText?.trim() || "";
    // A reply or a note may be nothing but attachments, but never a half-uploaded one.
    const ready = attachments.filter((a) => a.url && !a.uploading);
    if (attachments.length !== ready.length) {
      toast({
        title: "Wait for the attachments to finish uploading.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!message && !ready.length) {
      toast({
        title: "The message cannot be empty!",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // cqAPI retries POSTs, so a send that succeeded but lost its response must be
    // recognised on the retry rather than posted to the customer a second time.
    // Scoped by ticket AND mode: reusing a failed note's id for a reply would make
    // the server replay the note and never send the reply.
    const scope = sendScope(mode);
    const sentMentions = mode === "note" ? [...mentioned] : [];
    // The files AND the recipients are part of what was sent, so a retry only counts as
    // the same attempt when both are the same. Mentions were missing from this identity:
    // a note that committed with a lost response, retried with the same words but a
    // corrected recipient, reused the old id — the server replayed the stored note and
    // the corrected colleague was never notified.
    const attachmentKey = ready.map((a) => a.url).join("|");
    const mentionKey = sentMentions.map((m) => String(m._id)).sort().join("|");
    const clientMessageId =
      retryRef.current &&
      retryRef.current.scope === scope &&
      retryRef.current.body === message &&
      retryRef.current.attachmentKey === attachmentKey &&
      retryRef.current.mentionKey === mentionKey
        ? retryRef.current.clientMessageId
        : newClientMessageId();
    retryRef.current = { scope, body: message, attachmentKey, mentionKey, clientMessageId };

    setSending(true);

    // The composer is CLEARED at send, like the widget's. It used to stay populated and
    // editable, with success reconciling by heuristics — "clear the text if unchanged",
    // "consume the sent files/mentions by id" — and every heuristic had a corner: a
    // re-tagged colleague consumed by the previous note's success, a mid-send draft
    // discarding a failure's only copy. Clearing at send removes the shared state
    // entirely: whatever is in the box after this line belongs to the NEXT message, and
    // a failure restores the captured send whole (below) rather than negotiating with
    // what the agent typed meanwhile.
    const sent = {
      scope,
      mode,
      message,
      attachments: ready,
      mentions: sentMentions,
      attachmentKey,
      mentionKey,
      clientMessageId,
    };
    // Whatever the composer was holding is now superseded — by this very message going
    // out again under the same id, or by the rewrite the agent sent instead. Either way
    // the old record has done its job and must not outlive the composer that held it.
    if (composerEntryRef.current) {
      dropSend(composerEntryRef.current);
      composerEntryRef.current = null;
    }
    // Recorded BEFORE the await, because the composer is about to be emptied and this
    // becomes the only copy.
    const held = recordSend(scope, sent);

    if (composerRef.current) {
      composerRef.current.innerText = "";
      adjustComposerHeight();
    }
    setAttachments([]);
    setAttachError("");
    setMentioned([]);
    setSuggestions([]);
    setPlaceholderVisible(true);
    // The failure path reads these mirrors, and the failure can land before React
    // commits the clears above (a rejection in the same tick) — the effect that
    // normally maintains them has not run yet. Set them with the clear itself.
    attachmentsLiveRef.current = [];
    mentionedLiveRef.current = [];

    try {
      const response =
        mode === "note"
          ? await cqAPI.post(
              `/support/inbox/${ticketId}/notes`,
              {
                message,
                clientMessageId,
                mentions: sentMentions.map((m) => m._id),
                attachments: ready.map(attachmentPayload),
              },
              WRITE_TIMEOUT_MS
            )
          : await cqAPI.post(
              `/support/inbox/${ticketId}/reply`,
              {
                message,
                clientMessageId,
                attachments: ready.map(attachmentPayload),
                // The customer's last word as this screen has it. A reply marks the
                // thread handled and seen by whoever sent it, so one written against a
                // view the customer has moved on from says an agent read something they
                // never saw. The server refuses in that case and hands back the thread.
                lastCustomerMessageAt: ticket?.last_customer_message_at ?? null,
                // ...and which message that was, since two sent in the same millisecond
                // share a timestamp and the time alone would match either.
                lastCustomerMessageId: ticket?.last_customer_message_id ?? null,
              },
              WRITE_TIMEOUT_MS
            );

      if (response?.type === "ok" && response.data) {
        // Stored by the server: the record has done its job.
        dropSend(held);
        if (!mountedRef.current) return;
        // The composer was cleared at send, so whatever it holds now is the next
        // message — nothing here touches it.
        if (retryRef.current?.clientMessageId === clientMessageId) retryRef.current = null;
        applyWrite(response.data);
        // The composer just emptied for good — a send held from earlier (one that failed
        // while a draft occupied the box) gets its promised restoration.
        maybeRestoreHeld();
      } else {
        // A refusal can carry the thread as it now stands — take it, so the agent is
        // reading the message that caused the refusal.
        if (response?.data && mountedRef.current) applyWrite(response.data);
        holdOrRestore(held, response?.msg || "Failed to send the message.");
      }
    } catch (error) {
      console.error("Error sending support message:", error);
      holdOrRestore(held, "Failed to send the message.");
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" width="100%" height="100vh">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  if (!ticket) {
    return (
      <Box p={6} width="100%">
        <Button variant="ghost" leftIcon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate("/support")}>
          Back to inbox
        </Button>
        <Text mt={4}>This ticket is not available.</Text>
      </Box>
    );
  }

  const renderAttachments = (message) => {
    const list = message.attachments || [];
    if (!list.length) return null;
    return (
      <Flex direction="column" align="flex-start" gap={1} mt={2}>
        {list.map((attachment, index) => {
          const key = `${message._id}-${attachment.ref ?? index}`;
          if (isImageAttachment(attachment)) {
            const blobUrl = attachmentBlobs[attachmentKeyFor(message, attachment)];
            return (
              <Box
                as="button"
                type="button"
                key={key}
                onClick={() => openAttachment(attachment, message)}
                title={attachment.name}
                maxW="220px"
                lineHeight={0}
                overflow="hidden"
                borderWidth="1px"
                borderColor={borderClr}
                borderRadius="10px"
              >
                {blobUrl ? (
                  <Image
                    src={blobUrl}
                    alt={attachment.name}
                    maxW="220px"
                    maxH="220px"
                    objectFit="contain"
                  />
                ) : (
                  <Flex align="center" justify="center" w="120px" h="80px">
                    <Spinner size="sm" />
                  </Flex>
                )}
              </Box>
            );
          }
          return (
            <Flex
              as="button"
              type="button"
              key={key}
              onClick={() => openAttachment(attachment, message)}
              align="center"
              wrap="wrap"
              gap={2}
              maxW="100%"
              textAlign="left"
              borderWidth="1px"
              borderColor={borderClr}
              borderRadius="8px"
              px={2}
              py={1}
            >
              <AttachmentIcon boxSize={3} />
              <Text fontSize="xs" wordBreak="break-word">
                {attachment.name}
              </Text>
              <Text fontSize="xs" opacity={0.7}>
                {formatBytes(attachment.size)}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const renderMessage = (message) => {
    const isCustomer = message.role === "user";
    const isNote = message.role === "internal_note";
    const author = authorLabel(message, ticket);
    const bg = isNote
      ? "orange.100"
      : isCustomer
      ? customerBg
      : message.role === "assistant"
      ? assistantBg
      : supportBg;

    return (
      <Flex
        key={message._id}
        direction="column"
        align={isNote ? "center" : isCustomer ? "flex-start" : "flex-end"}
        my={3}
      >
        <Box
          bg={bg}
          color={isNote ? "#000000" : undefined}
          borderRadius="12px"
          borderWidth={isNote ? "1px" : 0}
          borderColor={isNote ? "orange.400" : undefined}
          p={3}
          maxW={isNote ? "100%" : { base: "88%", md: "70%" }}
          width={isNote ? "100%" : undefined}
        >
          <Flex align="center" gap={2} mb={1} wrap="wrap">
            {message.role === "assistant" && <Box as={RiSparkling2Fill} aria-hidden />}
            {isNote && <Box as={RiStickyNoteLine} aria-hidden />}
            <Text fontSize="xs" fontWeight="bold">
              {author.role}
            </Text>
            {!!author.author && (
              <Text fontSize="xs" opacity={0.75}>
                · {author.author}
              </Text>
            )}
            <Text fontSize="xs" opacity={0.75}>
              {moment(message.created).format("MMM D, YYYY h:mm A")}
            </Text>
          </Flex>

          {isNote && (
            <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb={2}>
              {INTERNAL_NOTE_WARNING}
            </Text>
          )}

          {/* A message can be attachments only, and an empty line above them
              would just look like something failed to render. */}
          {!!String(message.body || "").trim() && (
            <Text
              as="div"
              className="support-message-body"
              fontSize="sm"
              dangerouslySetInnerHTML={{ __html: sanitizedBody(message) }}
            />
          )}

          {renderAttachments(message)}

          {isCustomer && message.via === "email" && (
            // They answered the notification instead of opening the app, so they are
            // not looking at it — a deep link in the reply would be no use to them.
            <Text fontSize="xs" opacity={0.7} mt={2} textAlign="left">
              replied by email
            </Text>
          )}

          {isCustomer && message.via !== "email" && message.page_context?.path && (
            <Tooltip label={message.page_context.url || message.page_context.path} hasArrow>
              <Text fontSize="xs" opacity={0.7} mt={2} textAlign="left">
                sent from {message.page_context.path}
              </Text>
            </Tooltip>
          )}

          {message.links?.length > 0 && (
            <Flex wrap="wrap" gap={2} mt={2}>
              {message.links.map((link) => (
                <Tag key={link.path} size="sm" variant="subtle" colorScheme="blue">
                  {link.label} · {link.path}
                </Tag>
              ))}
            </Flex>
          )}

          {isNote && message.mentions?.length > 0 && (
            <Text fontSize="xs" opacity={0.8} mt={2} textAlign="left">
              Notified: {message.mentions.map((m) => m.name).filter(Boolean).join(", ")}
            </Text>
          )}
        </Box>
      </Flex>
    );
  };

  const isNoteMode = mode === "note";

  // A thread the assistant was never going to answer — anything off the `ai` status,
  // or one that was escalated — still needs an operable switch, because an agent
  // pauses pre-emptively and has to see the value that is actually stored. So the
  // difference is said out loud instead of being baked into a disabled control.
  const pausedBy = ticket.ai_paused_by?.name;
  const aiPausedHint = ticket.ai_paused
    ? ticket.ai_paused_at &&
      // Who as well as when: several superadmins work the same queue, and the next
      // one along needs to know whose thread they would be stepping into.
      `Paused ${pausedBy ? `by ${pausedBy} ` : ""}${moment(ticket.ai_paused_at).format("MMM D, YYYY h:mm A")}`
    : (ticket.status !== "ai" || !!ticket.escalated_at) &&
      "The assistant would not answer this thread anyway";

  return (
    <Flex ref={pageRef} className="support-ticket" direction="column" width="100%" bg={pageBg} borderRadius="md">
      {/* Header */}
      <Box bg={panelBg} borderWidth="1px" borderColor={borderClr} borderRadius="md" p={3} flexShrink={0}>
        <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
          <Box textAlign="left" minW={0} flex="1 1 240px">
            <Button
              variant="ghost"
              size="sm"
              pl={0}
              leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              // Held while a write is in flight, like every other control here. This
              // page's state dies with the component: leave during a send and a FAILURE
              // landing after the unmount has nowhere to restore the text, the files or
              // the retry token to — the agent's message is simply gone, and a send that
              // committed with a lost response would be re-sent later under a fresh id.
              // The pending state cannot survive the route change, so the route change
              // waits the few seconds instead. (The browser's own back button is outside
              // this control; the in-app button is the path agents actually use.)
              isDisabled={writing}
              onClick={() => navigate("/support")}
            >
              Back to inbox
            </Button>
            <Text fontSize="lg" fontWeight="bold" mt={1} noOfLines={2}>
              {ticket.subject || "(no subject)"}
            </Text>
            <Text fontSize="sm" color={mutedColor} noOfLines={2}>
              {[ticket.business_name, ticket.user_name, ticket.user_email].filter(Boolean).join(" · ")}
              {ticket.customer_brand && ticket.customer_brand !== FIRST_PARTY_BRAND && (
                <Tag ml={2} size="sm" colorScheme="purple" variant="subtle">
                  sees this as {ticket.customer_brand}
                </Tag>
              )}
            </Text>
            {Array.isArray(ticket.participants) && ticket.participants.length > 0 && (
              <ParticipantsPanel
                ticket={ticket}
                mutedColor={mutedColor}
                writing={writing}
                saving={savingParticipants}
                directory={participantDirectory}
                directoryError={participantDirectoryError}
                loadingDirectory={loadingParticipantDirectory}
                onLoadDirectory={loadParticipantDirectory}
                onAdd={handleAddParticipant}
                onRemove={handleRemoveParticipant}
              />
            )}
            {ticket.outbound && (
              <OutboundSummary
                ticket={ticket}
                mutedColor={mutedColor}
                writing={writing}
                savingAction={savingAction}
                onOpen={(id) => navigate(`/support/${id}`)}
                onActionDone={handleActionDone}
              />
            )}
          </Box>
          <Flex align="center" gap={2} wrap="wrap">
            {ticket.source && (
              <Badge colorScheme="blue" textTransform="none">
                {ticket.source}
              </Badge>
            )}
            <Badge colorScheme={STATUS_COLORS[ticket.status] || "gray"}>{ticket.status}</Badge>
            <Select
              size="sm"
              width="200px"
              value={ticket.status || ""}
              isDisabled={writing}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {!STATUS_OPTIONS.some((option) => option.value === ticket.status) && (
                <option value={ticket.status || ""} disabled>
                  {STATUS_LABELS[ticket.status] || ticket.status || "—"}
                </option>
              )}
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Flex direction="column" align="flex-start" gap={1} minW={0}>
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel htmlFor="support-ai-paused" mb={0} fontSize="sm" whiteSpace="nowrap">
                  {ticket.ai_paused ? "AI paused" : "AI answering"}
                </FormLabel>
                <Switch
                  id="support-ai-paused"
                  isChecked={!!ticket.ai_paused}
                  isDisabled={writing}
                  onChange={(e) => handleAiPausedChange(e.target.checked)}
                />
              </FormControl>
              {!!aiPausedHint && (
                <Text fontSize="xs" color={mutedColor} textAlign="left">
                  {aiPausedHint}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>

      {/* Timeline */}
      <Box
        ref={timelineRef}
        flex="1 1 auto"
        minH={0}
        overflowY="auto"
        px={{ base: 2, md: 4 }}
        py={2}
        mt={2}
        bg={panelBg}
        borderWidth="1px"
        borderColor={borderClr}
        borderRadius="md"
      >
        {(ticket.messages || []).length === 0 ? (
          <Text fontSize="sm" color={mutedColor} py={6}>
            No messages yet.
          </Text>
        ) : (
          (ticket.messages || []).map(renderMessage)
        )}
      </Box>

      {/* Composer */}
      <Box
        mt={2}
        p={3}
        flexShrink={0}
        borderWidth="1px"
        borderColor={isNoteMode ? "orange.400" : borderClr}
        borderRadius="md"
        bg={isNoteMode ? "orange.100" : panelBg}
        color={isNoteMode ? "#000000" : undefined}
      >
        <Flex align="center" justify="space-between" gap={2} wrap="wrap" mb={2}>
          <HStack spacing={1}>
            <Button
              size="xs"
              variant={isNoteMode ? "ghost" : "solid"}
              colorScheme={isNoteMode ? "gray" : "blue"}
              onClick={() => switchMode("reply")}
            >
              Reply
            </Button>
            <Button
              size="xs"
              variant={isNoteMode ? "solid" : "ghost"}
              colorScheme="orange"
              leftIcon={<Box as={RiStickyNoteLine} aria-hidden />}
              onClick={() => switchMode("note")}
            >
              Internal note
            </Button>
          </HStack>
          {isNoteMode && (
            <Text fontSize="xs" fontWeight="semibold" color="orange.700">
              {INTERNAL_NOTE_WARNING}
            </Text>
          )}
        </Flex>

        {isNoteMode && (mentioned.length > 0 || suggestions.length > 0) && (
          <Flex wrap="wrap" align="center" mb={2} minH="28px">
            {mentioned.map((member) => (
              <div key={member._id} className="support-mention-chip">
                {member.name}
                <IconButton
                  icon={<DeleteIcon />}
                  onClick={() => deleteMention(member)}
                  aria-label={`Remove ${member.name}`}
                  variant="none"
                  size="xs"
                />
              </div>
            ))}
            {suggestions.map((member) => (
              <div
                key={member._id}
                className="support-mention-chip"
                onClick={() => insertAgentNameAtCursor(member)}
              >
                {member.name}
              </div>
            ))}
          </Flex>
        )}

        <Divider mb={2} />

        {attachments.length > 0 && (
          <Flex wrap="wrap" gap={2} mb={2}>
            {attachments.map((attachment) => (
              <Flex
                key={attachment._id}
                align="center"
                gap={1}
                minW={0}
                maxW="100%"
                bg={isNoteMode ? "orange.50" : chipBg}
                borderWidth="1px"
                borderColor={borderClr}
                borderRadius="full"
                pl={2}
                pr="2px"
                py="2px"
              >
                {attachment.uploading ? (
                  <Spinner size="xs" flexShrink={0} />
                ) : (
                  <AttachmentIcon boxSize={3} flexShrink={0} />
                )}
                <Text fontSize="xs" noOfLines={1} maxW="160px">
                  {attachment.name}
                </Text>
                <Text fontSize="xs" opacity={0.7} flexShrink={0}>
                  {formatBytes(attachment.size)}
                </Text>
                <IconButton
                  icon={<SmallCloseIcon />}
                  // Not while it is still uploading. The upload cannot be called back:
                  // if S3 stores the object after the row is gone, nothing will ever
                  // reference it and nothing will remove it, leaving somebody's support
                  // file in private storage unreachable. It is a second or two, and the
                  // spinner says why.
                  onClick={() => removeAttachment(attachment._id)}
                  isDisabled={!!attachment.uploading}
                  aria-label={`Remove ${attachment.name}`}
                  variant="ghost"
                  size="xs"
                  minW="20px"
                  h="20px"
                  px={0}
                  borderRadius="full"
                />
              </Flex>
            ))}
          </Flex>
        )}

        {!!attachError && (
          <Text fontSize="xs" color="red.500" mb={2}>
            {attachError}
          </Text>
        )}

        <Flex align="flex-end" gap={2}>
          <Input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            hidden
            onChange={(event) => {
              const picked = Array.from(event.target.files || []);
              // Cleared so picking the same file again still fires onChange.
              event.target.value = "";
              attachFiles(picked);
            }}
          />
          <IconButton
            icon={<AttachmentIcon />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            isDisabled={writing || attachments.length >= MAX_ATTACHMENTS}
            aria-label="Attach a file"
            variant="ghost"
          />
          <Box position="relative" flex="1" minW={0}>
            <div
              contentEditable
              ref={composerRef}
              className="support-composer-input"
              id="support-composer"
              onInput={adjustComposerHeight}
              onPaste={handlePaste}
              onKeyUp={checkMention}
              onFocus={() => setPlaceholderVisible(false)}
              onBlur={handleBlur}
            />
            {placeholderVisible && (
              <Box
                position="absolute"
                top="0"
                left="0"
                opacity={0.6}
                pointerEvents="none"
                fontSize="sm"
              >
                {isNoteMode ? "Write an internal note. Use @ to notify a teammate." : "Write a reply to the customer..."}
              </Box>
            )}
          </Box>
          <Button
            colorScheme={isNoteMode ? "orange" : "blue"}
            onClick={handleSend}
            isLoading={sending}
            isDisabled={writing}
            aria-label="Send"
          >
            <ArrowForwardIcon />
          </Button>
        </Flex>
      </Box>

      <AlertDialog
        isOpen={!!pendingMode}
        leastDestructiveRef={cancelSwitchRef}
        onClose={() => setPendingMode(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Send this to the customer?
            </AlertDialogHeader>
            <AlertDialogBody>
              You wrote this as an internal note. Switching to Reply means these words
              {attachments.length > 0 ? " and the files attached to them" : ""} go to the
              customer and are emailed to them.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelSwitchRef} onClick={() => setPendingMode(null)}>
                Keep as note
              </Button>
              <Button colorScheme="blue" ml={3} onClick={confirmSwitchMode}>
                Switch to Reply
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}

/**
 * Everything the thread holds belongs to one ticket — the conversation it loaded,
 * the half-written reply, the files staged for it, the retry token, whether the
 * agent is writing to the customer or to the team. Keying on the id remounts all
 * of it, so none can outlive the ticket it was meant for: the reply a superadmin
 * started for one practice cannot reach another's, and a slow load for the ticket
 * they just left resolves into a torn-down tree instead of painting itself over
 * the one on screen. It also pins the id every request is issued against for the
 * life of the instance, so a send can only ever go to the thread being read.
 */
function SupportTicketDetail() {
  const { ticketId } = useParams();
  return <SupportTicketThread key={ticketId} ticketId={ticketId} />;
}

export default SupportTicketDetail;
